import React, { useMemo, useState } from 'react';

function JsonNode({ name, value, depth = 0, isLast = true, defaultOpen = true }) {
  const [open, setOpen] = useState(depth < 3 ? defaultOpen : false);
  const isObj = value && typeof value === 'object' && !Array.isArray(value);
  const isArr = Array.isArray(value);
  const renderKey = name !== undefined && (
    <span style={{ color: 'var(--yuno-purple-700)', fontWeight: 500 }}>"{name}"</span>
  );
  if (isObj || isArr) {
    const entries = isArr ? value.map((v, i) => [i, v]) : Object.entries(value);
    const open_b = isArr ? '[' : '{';
    const close_b = isArr ? ']' : '}';
    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 16 }}>
        <div onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--neutral-400)', fontSize: 10, display: 'inline-block', width: 10, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms' }}>▸</span>
          {renderKey}{name !== undefined && <span style={{ color: 'var(--neutral-500)' }}>: </span>}
          <span style={{ color: 'var(--neutral-600)' }}>{open_b}</span>
          {!open && <span style={{ color: 'var(--neutral-400)', fontStyle: 'italic' }}> {entries.length} {isArr ? 'items' : 'keys'} </span>}
          {!open && <span style={{ color: 'var(--neutral-600)' }}>{close_b}{isLast ? '' : ','}</span>}
        </div>
        {open && (
          <div style={{ borderLeft: '1px solid var(--border-subtle)', marginLeft: 4 }}>
            {entries.map(([k, v], i) => (
              <JsonNode key={k} name={isArr ? undefined : k} value={v} depth={depth + 1} isLast={i === entries.length - 1} />
            ))}
            <div style={{ color: 'var(--neutral-600)', paddingLeft: 16 }}>{close_b}{isLast ? '' : ','}</div>
          </div>
        )}
      </div>
    );
  }
  let rendered;
  if (value === null) rendered = <span style={{ color: 'var(--neutral-500)' }}>null</span>;
  else if (typeof value === 'string') rendered = <span style={{ color: '#0a7d44' }}>"{value}"</span>;
  else if (typeof value === 'number') rendered = <span style={{ color: '#b45309' }}>{value}</span>;
  else if (typeof value === 'boolean') rendered = <span style={{ color: 'var(--info-600)' }}>{String(value)}</span>;
  else rendered = <span>{String(value)}</span>;
  return (
    <div style={{ paddingLeft: 16 }}>
      {renderKey}<span style={{ color: 'var(--neutral-500)' }}>: </span>{rendered}{isLast ? '' : <span style={{ color: 'var(--neutral-500)' }}>,</span>}
    </div>
  );
}

export function JsonTree({ value }) {
  if (value == null) return <Empty />;
  return (
    <div style={{
      fontFamily: 'var(--font-mono)', fontSize: 12.5, lineHeight: 1.65,
      color: 'var(--neutral-800)',
    }}>
      <JsonNode value={value} depth={0} />
    </div>
  );
}

export function JsonRaw({ value, raw }) {
  const text = useMemo(() => {
    if (value != null) return JSON.stringify(value, null, 2);
    return raw || '';
  }, [value, raw]);
  const html = useMemo(() => {
    const esc = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (value == null) return esc; // raw text — no JSON syntax highlight
    return esc
      .replace(/("(?:\\.|[^"\\])*")(\s*:)/g, '<span style="color:var(--yuno-purple-700);font-weight:500">$1</span>$2')
      .replace(/: ("(?:\\.|[^"\\])*")/g, ': <span style="color:#0a7d44">$1</span>')
      .replace(/: (true|false)/g, ': <span style="color:var(--info-600)">$1</span>')
      .replace(/: (null)/g, ': <span style="color:var(--neutral-500)">$1</span>')
      .replace(/: (-?\d+\.?\d*)/g, ': <span style="color:#b45309">$1</span>');
  }, [text, value]);
  if (!text) return <Empty />;
  return (
    <pre style={{
      margin: 0, padding: 16,
      fontFamily: 'var(--font-mono)', fontSize: 12.5, lineHeight: 1.65,
      color: 'var(--neutral-800)', background: 'var(--neutral-25)',
      borderRadius: 8, border: '1px solid var(--border-subtle)',
      overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }} dangerouslySetInnerHTML={{ __html: html }} />
  );
}

function Empty() {
  return <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>No body</div>;
}
